import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import { DocumentArrowDownIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../hooks/useToast';
import { formatCursoLabel, formatCursoNombreAnio } from '../../utils/cursoLabel';

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  anio?: number;
  tipoEvaluacion: string;
  cantidadTemas: number;
}

interface AlumnoReporte {
  alumno: {
    id: number;
    nombre: string;
    apellido: string;
    numeroAlumno: string;
  };
  estado: 'aprobado' | 'desaprobado' | 'ausente';
  notasPorTema: string[];
  notaCompleta: string | null;
  observaciones: string;
}

interface ReporteGuarani {
  curso: Curso;
  alumnos: AlumnoReporte[];
  totalAlumnos: number;
  aprobados: number;
  desaprobados: number;
  ausentes: number;
  fechaGeneracion: string;
}

const ReporteGuaraniPage: React.FC = () => {
  const [selectedCurso, setSelectedCurso] = useState<number | null>(null);
  const { showSuccess, showError } = useToast();

  // Fetch cursos
  const { data: cursos, isLoading: loadingCursos } = useQuery({
    queryKey: ['cursos'],
    queryFn: async () => {
      try {
        const response = await axios.get(`/cursos`);
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching cursos:', error);
        return [];
      }
    },
  });

  // Fetch reporte
  const { data: reporte, isLoading: loadingReporte } = useQuery<ReporteGuarani>({
    queryKey: ['reporte-guarani', selectedCurso],
    queryFn: async () => {
      if (!selectedCurso) return null;
      try {
        const response = await axios.get(`/notas/curso/${selectedCurso}/reporte-guarani`);
        return response.data;
      } catch (error) {
        console.error('Error fetching reporte:', error);
        return null;
      }
    },
    enabled: !!selectedCurso,
  });

  // Generar PDF
  const handleGenerarPDF = async () => {
    if (!reporte) return;

    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      // Configuración de fuentes
      doc.setFont('helvetica');

      // Título principal
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('REPORTE PARA GUARANÍ', 105, 20, { align: 'center' });

      // Información del curso
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Curso: ${formatCursoLabel(reporte.curso)}`, 105, 35, { align: 'center' });
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Código: ${reporte.curso.codigo}`, 105, 42, { align: 'center' });

      // Resumen
      let currentY = 55;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('RESUMEN:', 20, currentY);
      currentY += 8;
      doc.setFont('helvetica', 'normal');
      doc.text(`Total de alumnos: ${reporte.totalAlumnos}`, 25, currentY);
      currentY += 6;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 128, 0);
      doc.text(`Aprobados: ${reporte.aprobados}`, 25, currentY);
      currentY += 6;
      doc.setTextColor(255, 0, 0);
      doc.text(`Desaprobados: ${reporte.desaprobados}`, 25, currentY);
      currentY += 6;
      doc.setTextColor(128, 128, 128);
      doc.text(`Ausentes: ${reporte.ausentes}`, 25, currentY);
      doc.setTextColor(0, 0, 0);
      currentY += 10;

      // Tabla de alumnos
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      
      if (reporte.curso.tipoEvaluacion === 'nota_por_tema' && reporte.curso.cantidadTemas >= 3) {
        // Tabla con notas por tema
        const headers = ['#', 'Legajo', 'Apellido', 'Nombre', 'T1', 'T2', 'T3', 'Estado'];
        const colWidths = [8, 25, 40, 40, 15, 15, 15, 30];
        let x = 10;
        let tableY = currentY;

        // Dibujar encabezados
        headers.forEach((header, index) => {
          doc.rect(x, tableY, colWidths[index], 8);
          doc.text(header, x + colWidths[index] / 2, tableY + 5, { align: 'center' });
          x += colWidths[index];
        });

        tableY += 8;

        // Datos de alumnos
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        reporte.alumnos.forEach((alumno, index) => {
          if (tableY > 280) {
            doc.addPage();
            tableY = 20;
          }

          x = 10;
          const estadoColor = alumno.estado === 'aprobado' ? [0, 128, 0] : 
                             alumno.estado === 'desaprobado' ? [255, 0, 0] : [128, 128, 128];
          
          const rowData = [
            (index + 1).toString(),
            alumno.alumno.numeroAlumno,
            alumno.alumno.apellido,
            alumno.alumno.nombre,
            alumno.notasPorTema[0] || '-',
            alumno.notasPorTema[1] || '-',
            alumno.notasPorTema[2] || '-',
            alumno.estado.toUpperCase(),
          ];

          rowData.forEach((cell, cellIndex) => {
            doc.rect(x, tableY, colWidths[cellIndex], 7);
            if (cellIndex === headers.length - 1) {
              doc.setTextColor(estadoColor[0], estadoColor[1], estadoColor[2]);
            }
            const cellText = cell.toString();
            const maxWidth = colWidths[cellIndex] - 2;
            const truncatedText = doc.splitTextToSize(cellText, maxWidth);
            doc.text(truncatedText, x + colWidths[cellIndex] / 2, tableY + 4, { align: 'center', maxWidth: maxWidth });
            doc.setTextColor(0, 0, 0);
            x += colWidths[cellIndex];
          });

          tableY += 7;
        });
      } else {
        // Tabla simple con nota completa
        const headers = ['#', 'Legajo', 'Apellido', 'Nombre', 'Nota', 'Estado'];
        const colWidths = [8, 30, 50, 50, 20, 30];
        let x = 10;
        let tableY = currentY;

        // Dibujar encabezados
        headers.forEach((header, index) => {
          doc.rect(x, tableY, colWidths[index], 8);
          doc.text(header, x + colWidths[index] / 2, tableY + 5, { align: 'center' });
          x += colWidths[index];
        });

        tableY += 8;

        // Datos de alumnos
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        reporte.alumnos.forEach((alumno, index) => {
          if (tableY > 280) {
            doc.addPage();
            tableY = 20;
          }

          x = 10;
          const estadoColor = alumno.estado === 'aprobado' ? [0, 128, 0] : 
                             alumno.estado === 'desaprobado' ? [255, 0, 0] : [128, 128, 128];
          
          const rowData = [
            (index + 1).toString(),
            alumno.alumno.numeroAlumno,
            alumno.alumno.apellido,
            alumno.alumno.nombre,
            alumno.notaCompleta || '-',
            alumno.estado.toUpperCase(),
          ];

          rowData.forEach((cell, cellIndex) => {
            doc.rect(x, tableY, colWidths[cellIndex], 7);
            if (cellIndex === headers.length - 1) {
              doc.setTextColor(estadoColor[0], estadoColor[1], estadoColor[2]);
            }
            const cellText = cell.toString();
            const maxWidth = colWidths[cellIndex] - 2;
            const truncatedText = doc.splitTextToSize(cellText, maxWidth);
            doc.text(truncatedText, x + colWidths[cellIndex] / 2, tableY + 4, { align: 'center', maxWidth: maxWidth });
            doc.setTextColor(0, 0, 0);
            x += colWidths[cellIndex];
          });

          tableY += 7;
        });
      }

      // Pie de página
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generado el: ${new Date().toLocaleString('es-AR')}`, 10, 290);
      doc.text(`Total: ${reporte.totalAlumnos} | Aprobados: ${reporte.aprobados} | Desaprobados: ${reporte.desaprobados} | Ausentes: ${reporte.ausentes}`, 105, 290, { align: 'center' });

      // Guardar PDF
      const fileName = `Reporte_Guarani_${formatCursoNombreAnio(reporte.curso).replace(/\s+/g, '_')}.pdf`;
      doc.save(fileName);

      showSuccess('PDF generado', 'El reporte se ha descargado correctamente');
    } catch (error: any) {
      console.error('Error al generar PDF:', error);
      showError('Error', 'No se pudo generar el PDF');
    }
  };

  // Generar CSV
  const handleGenerarCSV = () => {
    if (!reporte) return;

    try {
      const headers = ['Legajo', 'Apellido', 'Nombre', 'Estado'];
      if (reporte.curso.tipoEvaluacion === 'nota_por_tema' && reporte.curso.cantidadTemas >= 3) {
        headers.splice(3, 0, 'T1', 'T2', 'T3');
      } else {
        headers.splice(3, 0, 'Nota');
      }

      const rows = reporte.alumnos.map(alumno => {
        const row = [
          alumno.alumno.numeroAlumno,
          alumno.alumno.apellido,
          alumno.alumno.nombre,
        ];

        if (reporte.curso.tipoEvaluacion === 'nota_por_tema' && reporte.curso.cantidadTemas >= 3) {
          row.push(
            alumno.notasPorTema[0] || '-',
            alumno.notasPorTema[1] || '-',
            alumno.notasPorTema[2] || '-'
          );
        } else {
          row.push(alumno.notaCompleta || '-');
        }

        row.push(alumno.estado.toUpperCase());
        return row;
      });

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Crear y descargar archivo CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Reporte_Guarani_${formatCursoNombreAnio(reporte.curso).replace(/\s+/g, '_')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showSuccess('CSV generado', 'El archivo CSV se ha descargado correctamente');
    } catch (error: any) {
      console.error('Error al generar CSV:', error);
      showError('Error', 'No se pudo generar el CSV');
    }
  };

  if (loadingCursos) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando cursos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <ChartBarIcon className="h-8 w-8 mr-3 text-blue-600" />
          Reporte para Guaraní
        </h1>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seleccionar Curso
          </label>
          <select
            value={selectedCurso || ''}
            onChange={(e) => setSelectedCurso(e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Seleccionar curso</option>
            {Array.isArray(cursos) && cursos.map((curso: Curso) => (
              <option key={curso.id} value={curso.id}>
                {formatCursoLabel(curso)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCurso && (
        <>
          {loadingReporte ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Generando reporte...</p>
              </div>
            </div>
          ) : reporte ? (
            <>
              {/* Resumen */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {formatCursoNombreAnio(reporte.curso)} - Resumen
                  </h2>
                  <div className="flex gap-3">
                    <button
                      onClick={handleGenerarCSV}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                      Exportar CSV
                    </button>
                    <button
                      onClick={handleGenerarPDF}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                      Exportar PDF
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="text-sm font-medium text-blue-600">Total Alumnos</div>
                    <div className="text-3xl font-bold text-blue-900 mt-2">
                      {reporte.totalAlumnos}
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="text-sm font-medium text-green-600">Aprobados</div>
                    <div className="text-3xl font-bold text-green-900 mt-2">
                      {reporte.aprobados}
                    </div>
                    <div className="text-sm text-green-700 mt-1">
                      {reporte.totalAlumnos > 0 
                        ? `${((reporte.aprobados / reporte.totalAlumnos) * 100).toFixed(1)}%`
                        : '0%'}
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <div className="text-sm font-medium text-red-600">Desaprobados</div>
                    <div className="text-3xl font-bold text-red-900 mt-2">
                      {reporte.desaprobados}
                    </div>
                    <div className="text-sm text-red-700 mt-1">
                      {reporte.totalAlumnos > 0 
                        ? `${((reporte.desaprobados / reporte.totalAlumnos) * 100).toFixed(1)}%`
                        : '0%'}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-medium text-gray-600">Ausentes</div>
                    <div className="text-3xl font-bold text-gray-900 mt-2">
                      {reporte.ausentes}
                    </div>
                    <div className="text-sm text-gray-700 mt-1">
                      {reporte.totalAlumnos > 0 
                        ? `${((reporte.ausentes / reporte.totalAlumnos) * 100).toFixed(1)}%`
                        : '0%'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabla de alumnos */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Listado de Alumnos
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Legajo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Apellido
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nombre
                        </th>
                        {reporte.curso.tipoEvaluacion === 'nota_por_tema' && reporte.curso.cantidadTemas >= 3 && (
                          <>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              T1
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              T2
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              T3
                            </th>
                          </>
                        )}
                        {reporte.curso.tipoEvaluacion === 'nota_completa' && (
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nota
                          </th>
                        )}
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Observaciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reporte.alumnos.map((alumno, index) => {
                        const estadoColor = alumno.estado === 'aprobado' ? 'bg-green-100 text-green-800' :
                                          alumno.estado === 'desaprobado' ? 'bg-red-100 text-red-800' :
                                          'bg-gray-100 text-gray-800';
                        return (
                          <tr key={alumno.alumno.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {alumno.alumno.numeroAlumno}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {alumno.alumno.apellido}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {alumno.alumno.nombre}
                            </td>
                            {reporte.curso.tipoEvaluacion === 'nota_por_tema' && reporte.curso.cantidadTemas >= 3 && (
                              <>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                                  {alumno.notasPorTema[0] || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                                  {alumno.notasPorTema[1] || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                                  {alumno.notasPorTema[2] || '-'}
                                </td>
                              </>
                            )}
                            {reporte.curso.tipoEvaluacion === 'nota_completa' && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                                {alumno.notaCompleta || '-'}
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${estadoColor}`}>
                                {alumno.estado.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {alumno.observaciones}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <p className="text-gray-600 text-center">No hay datos disponibles para este curso.</p>
            </div>
          )}
        </>
      )}

      {!selectedCurso && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <p className="text-gray-600 text-center">Selecciona un curso para generar el reporte para Guaraní.</p>
        </div>
      )}
    </div>
  );
};

export default ReporteGuaraniPage;

